import React, { useMemo } from 'react';
import { InfoCallout, useNodeForm } from '../Components/NodeFormKit';
import { GUARD_TYPES, PARAM_TYPES, TOOL_TYPES, toSnake, uniqueToolName } from './agentCatalog';
import TemplateToolConfig from './TemplateToolConfig';
import FileToolConfig from './FileToolConfig';
import { AddLink, Field, InlineSwitch, PairListEditor, RemoveButton, SelectChip, SelectInput, TextArea, TextInput, csvToList, FormSection, ToggleField } from './AgentFields';

const toPairs = (pairs) => pairs || [];

const ParamCard = ({ param, onChange, onRemove, showSaveTo }) => {
  const { theme } = useNodeForm();
  const set = (patch) => onChange({ ...param, ...patch });
  return (
    <div style={{ border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '10px', backgroundColor: theme.panelBg, marginBottom: '8px' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <TextInput value={param.key} onChange={(value) => set({ key: toSnake(value) })} placeholder="dato" mono />
        </div>
        <div style={{ width: '110px' }}>
          <SelectInput value={param.type || 'string'} onChange={(value) => set({ type: value })} options={PARAM_TYPES} />
        </div>
        <RemoveButton onClick={onRemove} />
      </div>
      <div style={{ marginBottom: '8px' }}>
        <TextInput value={param.description} onChange={(value) => set({ description: value })} placeholder="Cómo pedirlo o qué es (lo lee la IA)" />
      </div>
      {param.type === 'enum' && (
        <div style={{ marginBottom: '8px' }}>
          <TextInput value={(param.values || []).join(', ')} onChange={(value) => set({ values: csvToList(value) })} placeholder="Opciones separadas por coma" />
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <InlineSwitch checked={param.required} onChange={(required) => set({ required })} label="Obligatorio" />
        {showSaveTo && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: theme.textTertiary, whiteSpace: 'nowrap' }}>Guardar en</span>
            <TextInput
              value={param.saveTo?.field}
              onChange={(value) => set({ saveTo: value ? { ...(param.saveTo || {}), field: value } : undefined })}
              placeholder="campo (opcional)"
              mono
            />
          </div>
        )}
      </div>
    </div>
  );
};

const GuardRow = ({ guard, onChange, onRemove, trackingLabel }) => {
  const set = (patch) => onChange({ ...guard, ...patch });
  const needsValues = guard.type === 'requiresValues' || guard.type === 'lastStatusIn';
  const options = GUARD_TYPES.map((option) =>
    option.value === 'noActive' || option.value === 'requiresActive'
      ? { ...option, label: option.label.replace('operación', trackingLabel) }
      : option
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <SelectInput value={guard.type} onChange={(value) => set({ type: value })} options={options} />
        </div>
        <RemoveButton onClick={onRemove} />
      </div>
      {needsValues && (
        <TextInput
          value={(guard.values || []).join(', ')}
          onChange={(value) => set({ values: csvToList(value) })}
          placeholder={guard.type === 'requiresValues' ? '{{nombre_usuario}}, {{latitude}}' : 'FINISHED'}
          mono
        />
      )}
      <TextInput value={guard.message} onChange={(value) => set({ message: value })} placeholder="Qué decirle a la IA si no se cumple" />
    </div>
  );
};

const ToolEditor = ({ tool, index, tools, onChange, flows, nodes = [], listAreas = [], listAdvisers = [], listTags = [], listStages = [], listLists = [], hasTracking, trackingLabel = 'operación' }) => {
  const { theme } = useNodeForm();
  const meta = TOOL_TYPES[tool.type] || TOOL_TYPES.node;
  const set = (patch) => onChange({ ...tool, ...patch });
  const setConfig = (patch) => set({ config: { ...(tool.config || {}), ...patch } });
  const setResult = (patch) => set({ result: { ...(tool.result || {}), ...patch } });
  const params = tool.params || [];
  const isHttp = tool.type === 'http' || tool.type === 'webhook';
  const returnsData = tool.type === 'node' || (isHttp && tool.config?.mode !== 'fire');
  const hasParams = ['node', 'http', 'webhook', 'saveField'].includes(tool.type);

  const queryApiNodes = useMemo(
    () =>
      nodes
        .filter((node) => node.type === 'queryApi')
        .map((node) => ({ value: node.id, label: `${node.data?.title || node.id}${node.data?.protocol?.id ? ` · ${node.data.protocol.id}` : ''}` })),
    [nodes]
  );

  const changeLabel = (label) => {
    const autoName = !tool.name || tool.name === uniqueToolName(tool.label || '', tools, index) || tool.name === toSnake(tool.label || '');
    set({ label, ...(autoName ? { name: uniqueToolName(label, tools, index) } : {}) });
  };

  const auth = tool.config?.auth || { type: 'none' };

  return (
    <div>
      <FormSection title="Herramienta" style={{ marginBottom: '14px' }}>
        <Field label="¿Cuándo debe usarla la IA?" hint="La IA decide con este texto. Escríbelo como se lo explicarías a una persona nueva.">
          <TextArea value={tool.whenToUse} onChange={(value) => set({ whenToUse: value, suggested: false })} placeholder="Cuando el contacto confirmó que quiere…" />
          {(meta.suggestions || []).filter((idea) => idea !== tool.whenToUse).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Ideas:</span>
              {(meta.suggestions || [])
                .filter((idea) => idea !== tool.whenToUse)
                .map((idea) => (
                  <SelectChip key={idea} selected={false} onClick={() => set({ whenToUse: idea, suggested: false })}>
                    {idea}
                  </SelectChip>
                ))}
            </div>
          )}
          {tool.suggested && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <span style={{ fontSize: '11px', color: theme.textSecondary }}>Es una sugerencia.</span>
              <button type="button" onClick={() => set({ suggested: false })} style={{ border: 'none', background: 'transparent', padding: 0, color: theme.textPrimary, fontSize: '11px', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                Marcar como revisada
              </button>
            </div>
          )}
        </Field>
        <Field label="Nombre visible">
          <TextInput value={tool.label} onChange={changeLabel} placeholder={meta.label} />
        </Field>
        <Field label="Nombre interno" hint="Se genera solo. Minúsculas y guion bajo." style={{ marginBottom: 0 }}>
          <TextInput value={tool.name} onChange={(value) => set({ name: toSnake(value) })} mono />
        </Field>
      </FormSection>

      {tool.type === 'node' && (
        <FormSection title="Qué ejecuta" style={{ marginBottom: '14px' }}>
          <Field label="Nodo Consulta API" hint="La herramienta ejecuta este nodo tal cual, con sus variables." style={{ marginBottom: 0 }}>
            <SelectInput
              value={tool.nodeId}
              onChange={(value) => set({ nodeId: value })}
              options={tool.nodeId && !queryApiNodes.some((option) => option.value === tool.nodeId) ? [...queryApiNodes, { value: tool.nodeId, label: `${tool.nodeId} · no está en el canvas` }] : queryApiNodes}
              placeholder="Seleccione un nodo"
            />
          </Field>
        </FormSection>
      )}

      {isHttp && (
        <FormSection title="Petición" style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {tool.type === 'http' && (
              <div style={{ width: '110px' }}>
                <Field label="Método">
                  <SelectInput value={tool.config?.method || 'GET'} onChange={(value) => setConfig({ method: value })} options={['GET', 'POST', 'PUT', 'DELETE'].map((value) => ({ value, label: value }))} />
                </Field>
              </div>
            )}
            <div style={{ flex: 1 }}>
              <Field label="URL" hint="Solo https. Usa {{param.dato}} para lo que llena la IA y {{variable}} para datos del contacto.">
                <TextInput value={tool.config?.url} onChange={(value) => setConfig({ url: value })} placeholder="https://" mono />
              </Field>
            </div>
          </div>
          <Field label="Autenticación">
            <SelectInput
              value={auth.type || 'none'}
              onChange={(value) => setConfig({ auth: { type: value } })}
              options={[
                { value: 'none', label: 'Ninguna' },
                { value: 'bearer', label: 'Bearer token' },
                { value: 'basic', label: 'Usuario y contraseña' },
                { value: 'header', label: 'Header secreto' },
                { value: 'hmac', label: 'Firma HMAC' },
              ]}
            />
          </Field>
          {auth.type === 'bearer' && (
            <Field label="Token">
              <TextInput value={auth.token} onChange={(value) => setConfig({ auth: { ...auth, token: value } })} mono />
            </Field>
          )}
          {auth.type === 'basic' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Field label="Usuario" style={{ flex: 1 }}>
                <TextInput value={auth.user} onChange={(value) => setConfig({ auth: { ...auth, user: value } })} />
              </Field>
              <Field label="Contraseña" style={{ flex: 1 }}>
                <TextInput value={auth.password} onChange={(value) => setConfig({ auth: { ...auth, password: value } })} type="password" />
              </Field>
            </div>
          )}
          {auth.type === 'header' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Field label="Header" style={{ flex: 1 }}>
                <TextInput value={auth.name} onChange={(value) => setConfig({ auth: { ...auth, name: value } })} placeholder="X-Api-Key" mono />
              </Field>
              <Field label="Valor" style={{ flex: 1 }}>
                <TextInput value={auth.value} onChange={(value) => setConfig({ auth: { ...auth, value } })} mono />
              </Field>
            </div>
          )}
          {auth.type === 'hmac' && (
            <Field label="Secreto" hint="Se envía X-SendingUp-Signature: t=<timestamp>,v1=<hmac sha256 de timestamp.body>">
              <TextInput value={auth.secret} onChange={(value) => setConfig({ auth: { ...auth, secret: value } })} mono />
            </Field>
          )}
          <Field label="Headers">
            <PairListEditor pairs={toPairs(tool.config?.headers)} onChange={(headers) => setConfig({ headers })} keyPlaceholder="Header" addLabel="Agregar header" />
          </Field>
          {(tool.config?.method || (tool.type === 'webhook' ? 'POST' : 'GET')) === 'GET' ? (
            <Field label="Parámetros de la URL">
              <PairListEditor pairs={toPairs(tool.config?.query)} onChange={(query) => setConfig({ query })} addLabel="Agregar parámetro" />
            </Field>
          ) : (
            <Field label="Cuerpo (JSON)" hint={tool.type === 'webhook' && !(tool.config?.body || []).length ? 'Vacío: se envían los datos de la herramienta más el nombre y teléfono del contacto.' : undefined}>
              <PairListEditor pairs={toPairs(tool.config?.body)} onChange={(body) => setConfig({ body })} addLabel="Agregar campo" />
            </Field>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Field label="Tiempo máximo (ms)" style={{ flex: 1, marginBottom: 0 }}>
              <TextInput value={tool.config?.timeoutMs} onChange={(value) => setConfig({ timeoutMs: Number(value) || undefined })} placeholder="8000" />
            </Field>
            {tool.type === 'webhook' && (
              <Field label="Modo" style={{ flex: 1, marginBottom: 0 }}>
                <SelectInput
                  value={tool.config?.mode || 'wait'}
                  onChange={(value) => setConfig({ mode: value })}
                  options={[
                    { value: 'wait', label: 'Esperar respuesta' },
                    { value: 'fire', label: 'Solo avisar' },
                  ]}
                />
              </Field>
            )}
          </div>
        </FormSection>
      )}

      {tool.type === 'transfer' && (
        <FormSection title="A quién transfiere" style={{ marginBottom: '14px' }}>
          <Field label="Modo">
            <SelectInput
              value={tool.config?.mode || 'automatic'}
              onChange={(value) => setConfig({ mode: value, value: undefined })}
              options={[
                { value: 'automatic', label: 'Automático (primera área o asesor)' },
                { value: 'area', label: 'Un área' },
                { value: 'agent', label: 'Un asesor' },
              ]}
            />
          </Field>
          {tool.config?.mode === 'area' && (
            <Field label="Área" style={{ marginBottom: 0 }}>
              <SelectInput value={tool.config?.value} onChange={(value) => setConfig({ value })} options={listAreas.map((area) => ({ value: area.label, label: area.label }))} placeholder="Seleccione" />
            </Field>
          )}
          {tool.config?.mode === 'agent' && (
            <Field label="Asesor" style={{ marginBottom: 0 }}>
              <SelectInput value={tool.config?.value} onChange={(value) => setConfig({ value })} options={listAdvisers.map((adviser) => ({ value: adviser.value, label: adviser.label }))} placeholder="Seleccione" />
            </Field>
          )}
        </FormSection>
      )}

      {tool.type === 'tag' && (
        <FormSection title="Etiquetas" style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {listTags.map((item) => {
              const selected = (tool.config?.tags || []).some((tag) => tag._id === item._id);
              return (
                <SelectChip
                  key={item._id}
                  selected={selected}
                  onClick={() =>
                    setConfig({
                      tags: selected
                        ? (tool.config?.tags || []).filter((tag) => tag._id !== item._id)
                        : [...(tool.config?.tags || []), { _id: item._id, name: item.title }],
                    })
                  }
                >
                  {item.title}
                </SelectChip>
              );
            })}
          </div>
          <ToggleField label="La IA elige una" hint="Si está apagado, se ponen todas las etiquetas marcadas." checked={tool.config?.choose} onChange={(choose) => setConfig({ choose })} last />
        </FormSection>
      )}

      {tool.type === 'paymentLink' && (
        <FormSection title="Cobro" style={{ marginBottom: '14px' }}>
          <Field label="Pasarela" hint="Usa las llaves configuradas en Métodos de pago de la cuenta.">
            <SelectInput
              value={tool.config?.platform}
              onChange={(value) => setConfig({ platform: value })}
              options={[
                { value: 'Epayco', label: 'ePayco' },
                { value: 'Wompi', label: 'Wompi' },
              ]}
              placeholder="Seleccione"
            />
          </Field>
          <Field label="Monto">
            <SelectInput
              value={tool.config?.amountMode || 'fixed'}
              onChange={(value) => setConfig({ amountMode: value })}
              options={[
                { value: 'fixed', label: 'Siempre el mismo monto' },
                { value: 'ai', label: 'La IA lo calcula según la conversación' },
              ]}
            />
          </Field>
          {tool.config?.amountMode === 'ai' ? (
            <Field label="Monto máximo (COP)" hint="La IA nunca podrá cobrar más que esto.">
              <TextInput value={tool.config?.maxAmount} onChange={(value) => setConfig({ maxAmount: value.replace(/[^\d]/g, '') })} placeholder="500000" />
            </Field>
          ) : (
            <Field label="Monto a cobrar (COP)">
              <TextInput value={tool.config?.amount} onChange={(value) => setConfig({ amount: value.replace(/[^\d]/g, '') })} placeholder="150000" />
            </Field>
          )}
          <Field label="Concepto" hint="Vacío = la IA lo escribe según lo que se cobra." style={{ marginBottom: 0 }}>
            <TextInput value={tool.config?.title} onChange={(value) => setConfig({ title: value })} placeholder="Reserva de apartamento" />
          </Field>
        </FormSection>
      )}

      {tool.type === 'template' && <TemplateToolConfig tool={tool} setConfig={setConfig} />}

      {tool.type === 'sendFile' && <FileToolConfig tool={tool} setConfig={setConfig} />}

      {tool.type === 'catalog' && (
        <FormSection title="Mensaje" style={{ marginBottom: '14px' }}>
          <Field label="Texto que acompaña el botón" hint="Requiere que la cuenta tenga un catálogo de WhatsApp conectado." style={{ marginBottom: 0 }}>
            <TextInput value={tool.config?.message} onChange={(value) => setConfig({ message: value })} placeholder="Mira nuestro catálogo 👇" />
          </Field>
        </FormSection>
      )}

      {tool.type === 'locationLink' && (() => {
        const mode = tool.config?.mode || 'destination';
        const fieldRow = (key, title) => {
          const value = tool.config?.[key] || {};
          const update = (patch) => setConfig({ [key]: { ...value, ...patch } });
          return (
            <Field label={title} hint="Nombres de campos adicionales del contacto donde se guardan latitud, longitud y dirección.">
              <div style={{ display: 'flex', gap: '6px' }}>
                <TextInput value={value.lat} onChange={(lat) => update({ lat })} placeholder="latitud" mono />
                <TextInput value={value.lng} onChange={(lng) => update({ lng })} placeholder="longitud" mono />
                <TextInput value={value.address} onChange={(address) => update({ address })} placeholder="dirección" mono />
              </div>
            </Field>
          );
        };
        return (
          <FormSection title="Mapa" style={{ marginBottom: '14px' }}>
            <Field label="Qué marca el contacto">
              <SelectInput
                value={mode}
                onChange={(value) => setConfig({ mode: value })}
                options={[
                  { value: 'destination', label: 'Solo el destino' },
                  { value: 'origin', label: 'Solo el origen' },
                  { value: 'origin_destination', label: 'Origen y destino' },
                ]}
              />
            </Field>
            {mode !== 'destination' && fieldRow('originFields', 'Campos del origen')}
            {mode !== 'origin' && fieldRow('fields', 'Campos del destino')}
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.45 }}>
              Cuando el contacto marque el punto, se guarda en esos campos y el agente sigue la conversación solo.
            </div>
          </FormSection>
        );
      })()}

      {(tool.type === 'stage' || tool.type === 'list') && (() => {
        const isStage = tool.type === 'stage';
        const key = isStage ? 'stages' : 'lists';
        const source = isStage ? listStages : listLists;
        const chosen = tool.config?.[key] || [];
        return (
          <FormSection title={isStage ? 'Etapas del embudo' : 'Listas'} style={{ marginBottom: '14px' }}>
            {!source.length && (
              <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                {isStage ? 'No hay etapas creadas en el CRM.' : 'No hay listas creadas.'}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {source.map((item) => {
                const selected = chosen.some((entry) => entry._id === item._id);
                return (
                  <SelectChip
                    key={item._id}
                    selected={selected}
                    onClick={() =>
                      setConfig({
                        [key]: selected ? chosen.filter((entry) => entry._id !== item._id) : [...chosen, { _id: item._id, name: item.name }],
                      })
                    }
                  >
                    {item.name}
                  </SelectChip>
                );
              })}
            </div>
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.45 }}>
              {chosen.length > 1
                ? `La IA elige ${isStage ? 'la etapa' : 'la lista'} según la conversación.`
                : `Con una sola opción, la IA siempre usa esa ${isStage ? 'etapa' : 'lista'}.`}
            </div>
          </FormSection>
        );
      })()}

      {hasParams && (
        <FormSection title="Datos que la IA debe conseguir" style={{ marginBottom: '14px' }}>
          {params.map((param, i) => (
            <ParamCard
              key={i}
              param={param}
              showSaveTo
              onChange={(next) => set({ params: params.map((p, j) => (j === i ? next : p)) })}
              onRemove={() => set({ params: params.filter((_, j) => j !== i) })}
            />
          ))}
          <AddLink onClick={() => set({ params: [...params, { key: '', type: 'string', required: false, description: '' }] })}>Agregar dato</AddLink>
        </FormSection>
      )}

      {returnsData && (
        <FormSection title="Resultado" style={{ marginBottom: '14px' }}>
          <Field label="Qué le devuelve a la IA" hint="Campos del resultado separados por coma. Tokens y URLs nunca viajan a la IA.">
            <TextInput value={(tool.result?.fields || []).join(', ')} onChange={(value) => setResult({ fields: csvToList(value) })} placeholder="status, message" mono />
          </Field>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Field label="Funcionó si el campo" style={{ flex: 1 }}>
              <TextInput
                value={tool.result?.successWhen?.field}
                onChange={(value) => setResult({ successWhen: value ? { ...(tool.result?.successWhen || { exists: true }), field: value } : undefined })}
                placeholder="(respuesta 2xx)"
                mono
              />
            </Field>
            <Field label="Vale (vacío = existe)" style={{ flex: 1 }}>
              <TextInput
                value={(tool.result?.successWhen?.in || []).join(', ')}
                onChange={(value) => {
                  const values = csvToList(value);
                  setResult({ successWhen: { field: tool.result?.successWhen?.field, ...(values.length ? { in: values } : { exists: true }) } });
                }}
                mono
              />
            </Field>
          </div>
          <Field label="Qué decirle a la IA si funcionó">
            <TextInput value={tool.result?.successHint} onChange={(value) => setResult({ successHint: value })} placeholder="Confírmalo en una línea" />
          </Field>
          <Field label="Qué decirle si falló" style={{ marginBottom: hasTracking ? '14px' : 0 }}>
            <TextInput value={tool.result?.failureHint} onChange={(value) => setResult({ failureHint: value })} />
          </Field>
          {hasTracking && (
            <Field label="Seguimiento" style={{ marginBottom: 0 }}>
              <SelectInput
                value={tool.tracking || ''}
                onChange={(value) => set({ tracking: value || undefined })}
                options={[
                  { value: '', label: 'No afecta el seguimiento' },
                  { value: 'start', label: `Inicia un ${trackingLabel}` },
                  { value: 'end', label: `Termina el ${trackingLabel}` },
                ]}
              />
            </Field>
          )}
          {tool.type === 'http' || tool.type === 'webhook' ? (
            <Field label="Respuesta de ejemplo (para Probar)" hint="En la prueba, los POST no se envían: se usa esta respuesta." style={{ marginTop: '14px', marginBottom: 0 }}>
              <TextArea
                rows={3}
                value={tool.config?.sampleText ?? (tool.config?.sample ? JSON.stringify(tool.config.sample, null, 2) : '')}
                onChange={(text) => {
                  let sample = tool.config?.sample;
                  try {
                    sample = text.trim() ? JSON.parse(text) : undefined;
                  } catch (error) {}
                  setConfig({ sampleText: text, sample });
                }}
                placeholder='{"id": "123", "status": "ok"}'
              />
            </Field>
          ) : null}
        </FormSection>
      )}

      {tool.type !== 'askLocation' && tool.type !== 'askContact' && tool.type !== 'options' && (
        <FormSection title="Reglas antes de ejecutarla" style={{ marginBottom: '14px' }}>
          {(tool.guards || []).map((guard, i) => (
            <GuardRow
              key={i}
              guard={guard}
              trackingLabel={trackingLabel}
              onChange={(next) => set({ guards: tool.guards.map((g, j) => (j === i ? next : g)) })}
              onRemove={() => set({ guards: tool.guards.filter((_, j) => j !== i) })}
            />
          ))}
          <AddLink onClick={() => set({ guards: [...(tool.guards || []), { type: 'requiresValues', values: [] }] })}>Agregar regla</AddLink>
        </FormSection>
      )}

      <FormSection title="Seguridad">
        <ToggleField
          label="Confirmar con el contacto antes"
          hint="La IA pide un «sí» explícito antes de usarla."
          checked={tool.confirmBefore}
          onChange={(confirmBefore) => set({ confirmBefore })}
          last
        />
      </FormSection>

      {meta.hint && <InfoCallout title={meta.label}>{meta.hint}.</InfoCallout>}
    </div>
  );
};

export default ToolEditor;
